import React, { memo, useEffect, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import CssBaseline from '@mui/material/CssBaseline';
import services from 'services';
import styles, { globalStyles } from './App.styles';
import 'url-search-params-polyfill';
import { contextPath } from 'config/baseURL';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import Admin, { paths as adminPaths } from './Admin/Admin';
import Studio, { paths as studioPaths } from './Studio/Studio';
import { StyledEngineProvider } from '@mui/material/styles';

// global style overrides
const GlobalCss = withStyles(theme => globalStyles(theme))(() => null);

const localStorageKeys = {
  isDarkTheme: 'veezoo.isDarkTheme'
};

const App = ({ classes }) => {
  const location = useLocation();
  const history = useHistory();

  // specifies whether to use the dark theme
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  // the requesting user
  const [user, setUser] = useState(null);
  // the environment
  const [environment, setEnvironment] = useState(null);
  // the environment
  const [languages, setLanguages] = useState(null);

  useEffect(() => {
    const value = localStorage.getItem(localStorageKeys.isDarkTheme);
    setIsDarkTheme(value === 'true');
  }, []);

  const toggleTheme = () => {
    localStorage.setItem(localStorageKeys.isDarkTheme, (!isDarkTheme).toString());
    setIsDarkTheme(!isDarkTheme);
  };

  const getUser = async () => {
    const response = await services.getUserDeprecated();
    if (!response.success) {
      const params = new URLSearchParams();
      params.set('redirectTo', location.pathname.substring(1) + location.search + location.hash);
      return (window.location.href = contextPath + '/login?' + params.toString());
    }

    const user = response.data;
    if (!user.isSuperUser) {
      return (window.location.href = contextPath + '');
    }

    // only reaches this point if result is success and is super user.
    setUser(user);
  };

  const getEnvironment = async () => {
    const response = await services.getEnvironment();
    if (response.success) {
      const environment = response.data;
      setEnvironment(environment);
    }
  };

  const getLanguages = async () => {
    const response = await services.getLanguages();
    if (response.success) {
      setLanguages(response.data.data);
    }
  };

  // removes the 'authRedirect' parameter from the URL, which is present after an SSO authentication
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('authRedirect')) {
      params.delete('authRedirect');
      history.replace({
        pathname: location.pathname,
        search: '?' + params.toString(),
        hash: location.hash
      });
    }
  }, []);

  // fetches initial data
  useEffect(() => {
    getUser();
    getEnvironment();
    getLanguages();
  }, []);

  const css = (
    <>
      <CssBaseline />
      <GlobalCss />
    </>
  );

  return (
    <div className={classes.root}>
      <StyledEngineProvider injectFirst>
        <Switch>
          <Route path={adminPaths.root}>
            <Admin
              isDarkTheme={isDarkTheme}
              toggleTheme={toggleTheme}
              user={user}
              environment={environment}
              languages={languages}
            >
              {css}
            </Admin>
          </Route>
          <Route path={studioPaths.root}>
            <Studio
              isDarkTheme={isDarkTheme}
              toggleTheme={toggleTheme}
              user={user}
              environment={environment}
              languages={languages}
            >
              {css}
            </Studio>
          </Route>
          <Redirect to={studioPaths.root} />
        </Switch>
      </StyledEngineProvider>
    </div>
  );
};

export default memo(withStyles(styles)(App));
